import moment from "moment";

import { TFunction } from "utils";

import {
  COUNTRY_CODE,
  CREDIT_NOTE_ACCOUNT,
  CREDIT_NOTE_ACCOUNT_CODE,
  CREDIT_NOTE_ITEM_TYPES,
  CREDIT_NOTE_TAX_SCENARIOS,
  CURRENCY_CODE,
  CreditNoteItemType,
  LANGUAGE_CODE,
  WELTGOLD_UNILEVER_CARBON_PROGRAM_ID,
} from "consts";

import { GetPendingPaymentsResponseBody, PROGRAM_TYPE } from "models";

type PendingPaymentWithDetails = GetPendingPaymentsResponseBody["withBankingDetails"][number];

export type CreditNoteRow = {
  "Credit Note Date": string;
  "Credit Note Number": string;
  "Customer Name": string;
  "Billing Address": string;
  "Billing Street 2": string;
  "Billing City": string;
  "Billing State": string;
  "Billing Country": COUNTRY_CODE;
  "Billing Code": string;
  "Billing Phone": string;
  Email: string;
  "Currency Code": CURRENCY_CODE;
  "Is Inclusive Tax": boolean;
  Total: number;
  Notes: string;
  "Item Name": string;
  Quantity: number;
  "Item Desc": string;
  "Item Tax Amount": number;
  "Item Total": number;
  "Tax1 ID": string;
  "Item Tax": string;
  "Item Tax %": number;
  "Item Tax Type": string;
  SubTotal: number;
  "Template Name": string;
  "Item Price": number;
  Account: string;
  "Account Code": string;
  "VAT Treatment": string;
  "CF.Leistungsdatum": string;
  "Tax Registration Number": string;
  "is insetting": boolean;
  beneficiary_name: string;
  iban: string;
  amount: number | string;
  currency: string;
  reference: string;
};

const roundToTwoDecimals = (value: number) => Number(value.toFixed(2));

// Some programs require a different credits item name on the credit note; keyed by program id.
const CREDIT_ITEM_NAME_KEYS_BY_PROGRAM: Record<string, string> = {
  [WELTGOLD_UNILEVER_CARBON_PROGRAM_ID]: "creditNoteExport.itemName.quantifiedCredits",
};

// Absent a program-specific override, the credits item name depends on the farm's program type.
const CREDIT_ITEM_NAME_KEY_BY_PROGRAM_TYPE: Record<string, string> = {
  [PROGRAM_TYPE.INSETTING]: "creditNoteExport.itemName.creditsInsetting",
  [PROGRAM_TYPE.OFFSETTING]: "creditNoteExport.itemName.creditsOffsetting",
};

const getTemplateLanguage = (language: LANGUAGE_CODE) => (language === LANGUAGE_CODE.GERMAN ? LANGUAGE_CODE.GERMAN : LANGUAGE_CODE.ENGLISH);

const getTemplateName = (templateLanguage: LANGUAGE_CODE) =>
  templateLanguage === LANGUAGE_CODE.GERMAN ? "Deutsch - Standard Template" : "English - Standard Template";

export const buildCreditNoteRows = (
  payments: PendingPaymentWithDetails[],
  creditNoteNumbersByFarmId: Record<string, number>,
  t: TFunction,
  itemTypes: readonly CreditNoteItemType[] = CREDIT_NOTE_ITEM_TYPES
): CreditNoteRow[] => {
  const creditNoteDate = moment().format("YYYY-MM-DD");
  // Only when exporting every payment type does the farm-level paymentGross/paymentNet match this
  // credit note's total; a restricted item-type set (e.g. credits-only) must total just its own rows.
  const isFullExport = itemTypes.length === CREDIT_NOTE_ITEM_TYPES.length;

  return payments.flatMap((payment) => {
    const { bankingDetails, email, farmCountry, farmName, language, paymentGross, paymentNet, phoneNumber, programId, programIsInsetting } = payment;

    const creditNoteNumber = creditNoteNumbersByFarmId[payment.farmId];

    if (!creditNoteNumber) return [];

    const creditNoteNumberLabel = `GU-${creditNoteNumber}`;

    // Each year included in this payout (current + any rolled-over prior years) gets its own
    // credits/incentives/footprinting split, sourced from that year's own quantity/price.
    const rowsForFarm = payment.years.flatMap(({ year, creditQuantity, creditPrice, ...amounts }) =>
      itemTypes
        .map(({ netField, itemNameKey }) => ({
          year,
          itemNameKey:
            netField === "creditPaymentNet"
              ? CREDIT_ITEM_NAME_KEYS_BY_PROGRAM[programId] ??
                CREDIT_ITEM_NAME_KEY_BY_PROGRAM_TYPE[programIsInsetting ? PROGRAM_TYPE.INSETTING : PROGRAM_TYPE.OFFSETTING]
              : itemNameKey,
          netField,
          netAmount: amounts[netField],
          creditQuantity,
          creditPrice,
        }))
        .filter(({ netAmount }) => !!netAmount)
    );

    if (rowsForFarm.length === 0) return [];

    const taxScenario = CREDIT_NOTE_TAX_SCENARIOS[bankingDetails.taxRate];

    if (!taxScenario) {
      throw new Error(`No tax scenario mapped for tax rate ${bankingDetails.taxRate} (farm ${payment.farmId})`);
    }

    const totalNet = isFullExport ? paymentNet : rowsForFarm.reduce((sum, { netAmount }) => sum + netAmount, 0);
    const totalGross = isFullExport ? paymentGross : totalNet * (1 + taxScenario.itemTaxPercent / 100);

    const templateLanguage = getTemplateLanguage(language);

    const notes = [
      t("creditNoteExport.notes.ibanTransfer", { iban: bankingDetails.iban, lng: templateLanguage }),
      bankingDetails.taxType === "reverseCharge" ? t("creditNoteExport.notes.reverseCharge", { lng: templateLanguage }) : null,
    ]
      .filter(Boolean)
      .join("\n");

    return rowsForFarm.map(({ year, itemNameKey, netField, netAmount, creditQuantity, creditPrice }, index) => {
      const isFirstRowOfCreditNote = index === 0;
      const quantity = netField === "creditPaymentNet" ? creditQuantity : 1;
      const itemPrice = roundToTwoDecimals(netField === "creditPaymentNet" ? creditPrice : netAmount);

      return {
        "Credit Note Date": creditNoteDate,
        "Credit Note Number": creditNoteNumberLabel,
        "Customer Name": farmName,
        "Billing Address": bankingDetails.addressStreet,
        "Billing Street 2": "",
        "Billing City": bankingDetails.addressCity,
        "Billing State": bankingDetails.state,
        "Billing Country": farmCountry,
        "Billing Code": bankingDetails.addressZip,
        "Billing Phone": phoneNumber,
        Email: email,
        "Currency Code": CURRENCY_CODE.EUR,
        "Is Inclusive Tax": false,
        Total: roundToTwoDecimals(totalGross),
        Notes: notes,
        "Item Name": t(itemNameKey, { year, lng: templateLanguage }),
        Quantity: quantity,
        "Item Desc": "",
        "Item Tax Amount": roundToTwoDecimals((netAmount * taxScenario.itemTaxPercent) / 100),
        "Item Total": roundToTwoDecimals(netAmount),
        "Tax1 ID": taxScenario.tax1Id,
        "Item Tax": t(taxScenario.itemTaxKey, { lng: templateLanguage }),
        "Item Tax %": taxScenario.itemTaxPercent,
        "Item Tax Type": taxScenario.itemTaxType,
        SubTotal: roundToTwoDecimals(totalNet),
        "Template Name": getTemplateName(templateLanguage),
        "Item Price": itemPrice,
        Account: CREDIT_NOTE_ACCOUNT,
        "Account Code": CREDIT_NOTE_ACCOUNT_CODE,
        "VAT Treatment": taxScenario.vatTreatment,
        "CF.Leistungsdatum": creditNoteDate,
        "Tax Registration Number": bankingDetails.taxNumber,
        "is insetting": programIsInsetting,
        beneficiary_name: isFirstRowOfCreditNote ? bankingDetails.accountHolder : "",
        iban: isFirstRowOfCreditNote ? bankingDetails.iban : "",
        amount: isFirstRowOfCreditNote ? roundToTwoDecimals(totalGross) : "",
        currency: isFirstRowOfCreditNote ? CURRENCY_CODE.EUR : "",
        // reference uses the users's actual language, not the de/en-only templateLanguage
        reference: isFirstRowOfCreditNote ? `${t("bankingReference", { lng: language })} : ${creditNoteNumberLabel}` : "",
      };
    });
  });
};
